import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core'
import { Permission } from '@employer/app/config/permission.config'
import { EmployerNavigationService, ROUTES } from '@employer/app/services/navigation.service'
import { TabRowComponent, TabRowItem } from '../tab-row/tab-row.component'
import { Observable, combineLatest, map, of } from 'rxjs'

export type SectionId = 'openings' | 'engage' | 'account'

export interface SectionTab {
  label: string
  route: string
  queryParams?: Record<string, string>
  isVisible$: Observable<boolean>
  /**
   * Match the URL exactly rather than as a prefix. Needed where a tab's route is a
   * prefix of its siblings' — `/account` is the start of `/account/security`, so the
   * default subset match would leave Personal Information lit on every account tab.
   */
  exact?: boolean
}

const ARIA_LABELS: Record<SectionId, string> = {
  openings: 'Openings sections',
  engage: 'Pipelines sections',
  account: 'My Account sections',
}

/**
 * The tab row that replaced the Openings and Engage nav groups. Both collapsed to a
 * single leaf each (see `navigation.service.ts`), so the leaves they used to hold —
 * Templates, Applicant Search, Talent Pool, Prospects — are tabs here instead.
 *
 * These are real links, not a switched container: every destination keeps its own
 * route, guard and lazy module, so deep links and permissions behave exactly as they
 * did when each was a nav row. Only the way you reach them changed.
 *
 * Each section carries its own accent so the two screens read apart at a glance:
 * Engage takes the primary accent, Openings the secondary.
 *
 * My Account is the third section (CNCT-6812). Its details — personal information,
 * decision team settings, profile picture, security, notifications — each had their own
 * route all along, but nothing linked to them once the sidebar's My Account subtree was
 * removed, so /account showed personal information and nothing else. The tab row is how
 * you reach the rest again.
 *
 * The row itself is tab-row, the one every tabbed screen uses; this component only builds each
 * section's list and its visibility.
 */
@Component({
  selector: 'section-tabs',
  standalone: true,
  imports: [AsyncPipe, TabRowComponent],
  // The tab list is built once in ngOnInit and never reassigned, so there is
  // nothing for the default strategy to re-check on every tick.
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <tab-row
      [tabs]="(items$ | async) ?? []"
      [tone]="section === 'openings' ? 'secondary' : 'primary'"
      [bleed]="'page'"
      [ariaLabel]="ariaLabel"
    ></tab-row>
  `,
  styles: [
    `
      /* The row is drawn by tab-row; this host only builds the section's tab list. It is display:
         contents so tab-row's own box is what sits in the page — a block wrapper here would be the
         sticky row's parent on a phone, and a sticky element cannot travel outside its parent. */
      :host {
        display: contents;
      }
    `,
  ],
})
export class SectionTabsComponent implements OnInit {
  @Input({ required: true }) section!: SectionId

  /**
   * Built ONCE, in ngOnInit.
   *
   * This was previously a getter the template read directly. That is an infinite
   * change-detection loop: the getter returned a fresh array of fresh objects
   * holding freshly-constructed observables on every call, so each tick gave
   * *ngFor new identities to re-render and gave every async pipe a new source to
   * resubscribe to — and each new subscription emitting marked the view dirty
   * again. The tab list is static per section, so it is computed once and held.
   */
  tabs: SectionTab[] = []

  ariaLabel = ''

  /** The visible tabs, as tab-row takes them. */
  items$: Observable<TabRowItem[]> = of([])

  constructor(private navigationService: EmployerNavigationService) {}

  ngOnInit(): void {
    this.tabs = this.buildTabs()
    this.ariaLabel = ARIA_LABELS[this.section]
    this.items$ = combineLatest(this.tabs.map(tab => tab.isVisible$)).pipe(
      map(visible =>
        this.tabs.filter((_, i) => visible[i]).map(({ label, route, queryParams, exact }) => ({ id: route, label, route, queryParams, exact }))
      )
    )
  }

  private buildTabs(): SectionTab[] {
    switch (this.section) {
      case 'openings':
        return this.buildOpeningsTabs()
      case 'account':
        return this.buildAccountTabs()
      default:
        return this.buildEngageTabs()
    }
  }

  /** Manage is the landing tab, so the leaf row and this tab share a destination. */
  private buildOpeningsTabs(): SectionTab[] {
    return [
      {
        label: 'Manage',
        route: ROUTES.JOBS_MANAGE,
        isVisible$: this.navigationService.userHasPermission$(Permission.ViewJobs),
      },
      {
        label: 'Templates',
        route: ROUTES.JOBS_TEMPLATE,
        isVisible$: this.navigationService.userHasPermission$(Permission.ManageJobTemplates),
      },
      {
        label: 'Settings',
        route: ROUTES.OPENINGS_SETTINGS,
        isVisible$: this.navigationService.userHasPermission$(Permission.ManageCompanySettings),
      },
    ]
  }

  private buildEngageTabs(): SectionTab[] {
    const poolVisible$ = combineLatest([
      this.navigationService.userHasPermission$(Permission.ViewCandidatePool),
      this.navigationService.userHasPermission$(Permission.ManageCandidatePoolShares),
    ]).pipe(map(([view, share]) => view || share))

    return [
      {
        label: 'Pipelines',
        route: ROUTES.JOBS,
        isVisible$: this.navigationService.userHasPermission$(Permission.ViewJobs),
      },
      {
        label: 'Applicant Search',
        route: ROUTES.APPLICATIONS,
        isVisible$: this.navigationService.userHasPermission$(Permission.ViewApplications),
      },
      {
        label: 'Talent Pool',
        route: ROUTES.CANDIDATE_POOL,
        isVisible$: poolVisible$,
      },
      {
        label: 'Prospects',
        route: ROUTES.STUB_ENGAGE_PROSPECTS,
        isVisible$: combineLatest([
          this.navigationService.features$('employerEngageProspects'),
          this.navigationService.userHasPermission$(Permission.ViewCandidatePool),
        ]).pipe(map(([flagOn, canView]) => flagOn && canView)),
      },
      {
        label: 'Settings',
        route: ROUTES.ENGAGE_SETTINGS,
        isVisible$: this.navigationService.userHasPermission$(Permission.ManageCompanySettings),
      },
    ]
  }

  /**
   * My Account. These are the signed-in user's own details, so there is no permission to
   * check — every tab is visible to whoever is looking at their own account. Profile Video
   * keeps the feature flag it has always had.
   *
   * Personal Information matches exactly: its route is `/account`, the prefix every other
   * tab here extends.
   */
  private buildAccountTabs(): SectionTab[] {
    return [
      {
        label: 'Personal Information',
        route: ROUTES.USER_PERSONAL,
        exact: true,
        isVisible$: of(true),
      },
      {
        label: 'Decision Team Settings',
        route: ROUTES.HIRING_TEAM_SETTINGS,
        isVisible$: of(true),
      },
      {
        label: 'Profile Picture',
        route: ROUTES.USER_PIC,
        isVisible$: of(true),
      },
      {
        label: 'Profile Video',
        route: ROUTES.USER_VIDEO,
        isVisible$: this.navigationService.features$('employerUserProfileVideo'),
      },
      {
        label: 'Security',
        route: ROUTES.USER_SECURITY,
        isVisible$: of(true),
      },
      {
        label: 'Notifications',
        route: ROUTES.USER_NOTIFICATIONS,
        isVisible$: of(true),
      },
    ]
  }
}
